import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Search,
  PlusCircle,
  ChevronLeft,
  ChevronRight,
  Mail,
  Cloud,
  RefreshCw,
  Trash2,
  Eye,
  CheckCircle,
  AlertCircle,
  Server,
  Globe,
  Edit,
  X,
  Save,
  FileText,
  Copy,
  AlertTriangle,
  Info
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
  DialogDescription,
  DialogClose,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { EmailDomainsService, type EmailDomain } from '@/api/admin/emailDomains';

interface SuperAdminEmailDomainsTabProps {
  organisations: any[];
}

const SuperAdminEmailDomainsTab: React.FC<SuperAdminEmailDomainsTabProps> = ({
  organisations
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [orgFilter, setOrgFilter] = useState('all');
  const [verificationFilter, setVerificationFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);

  // State
  const [domains, setDomains] = useState<EmailDomain[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalItems, setTotalItems] = useState(0);
  const [actionLoading, setActionLoading] = useState(false);

  // Dialog states
  const [addDomainDialogOpen, setAddDomainDialogOpen] = useState(false);
  const [viewDetailsDialogOpen, setViewDetailsDialogOpen] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState<any>(null);
  const [showInstructions, setShowInstructions] = useState(false);
  const [instructionsDomain, setInstructionsDomain] = useState('');
  const [dnsInstructions, setDnsInstructions] = useState<any[]>([]);
  const [formattedInstructions, setFormattedInstructions] = useState('');

  // Edit mode states
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedEmail, setEditedEmail] = useState('');
  const [editedName, setEditedName] = useState('');

  // Form states
  const [domainForm, setDomainForm] = useState({
    domain: '',
    organisation_id: '',
    default_from_email: '',
    default_from_name: '',
    import_existing: false,
    dns_mode: 'cloudflare' as 'cloudflare' | 'manual'
  });

  const [errors, setErrors] = useState({
    domain: '',
    organisation_id: '',
    default_from_email: '',
    default_from_name: ''
  });

  // Fetch domains from database
  const fetchDomains = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Build filters
      const filters: any = {};
      if (orgFilter !== 'all') {
        filters.organisation_id = orgFilter;
      }
      if (verificationFilter === 'verified') {
        filters.is_verified = true;
      } else if (verificationFilter === 'pending') {
        filters.is_verified = false;
      }
      if (searchQuery) {
        filters.search = searchQuery;
      }

      const fetchedDomains = await EmailDomainsService.fetchDomains(filters);

      // Organisation names are already included in the API response from backend
      // Only fallback to organisations prop lookup if organisation_name is missing
      const domainsWithOrgNames = fetchedDomains.map(domain => ({
        ...domain,
        organisation_name: domain.organisation_name ||
                          organisations.find(o => o.id === domain.organisation_id)?.name ||
                          'Unknown'
      }));

      setDomains(domainsWithOrgNames);
      setTotalItems(domainsWithOrgNames.length);

    } catch (err: any) {
      console.error('Error fetching domains:', err);
      setError(err.message || 'An error occurred while fetching domains');
    } finally {
      setIsLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchDomains();
  }, [page, searchQuery, orgFilter, verificationFilter]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [searchQuery, orgFilter, verificationFilter]);

  // Reset form when opening add dialog
  useEffect(() => {
    if (addDomainDialogOpen) {
      setDomainForm({
        domain: '',
        organisation_id: '',
        default_from_email: '',
        default_from_name: '',
        import_existing: false,
        dns_mode: 'cloudflare'
      });
      setErrors({
        domain: '',
        organisation_id: '',
        default_from_email: '',
        default_from_name: ''
      });
    }
  }, [addDomainDialogOpen]);

  // Auto-populate default_from_email when domain changes
  useEffect(() => {
    if (domainForm.domain && !domainForm.default_from_email) {
      setDomainForm(prev => ({
        ...prev,
        default_from_email: `info@${domainForm.domain}`
      }));
    }
  }, [domainForm.domain]);

  // Auto-populate default_from_name from organisation
  useEffect(() => {
    if (domainForm.organisation_id && !domainForm.default_from_name) {
      const org = organisations.find(o => o.id === domainForm.organisation_id);
      if (org) {
        setDomainForm(prev => ({
          ...prev,
          default_from_name: org.name
        }));
      }
    }
  }, [domainForm.organisation_id, organisations]);

  // Calculate total pages
  const totalPages = Math.ceil(totalItems / pageSize);

  // Handle view domain details
  const handleViewDetails = (domain: any) => {
    setSelectedDomain(domain);
    setIsEditMode(false);
    setEditedEmail(domain.default_from_email || '');
    setEditedName(domain.default_from_name || '');
    setViewDetailsDialogOpen(true);
  };

  // Handle save domain updates
  const handleSaveDomainUpdates = async () => {
    if (!selectedDomain) return;

    try {
      setActionLoading(true);

      const updatedDomain = await EmailDomainsService.updateDomain(selectedDomain.id, {
        default_from_email: editedEmail,
        default_from_name: editedName
      });

      toast({
        title: 'Success',
        description: 'Domain default sender information updated successfully',
      });

      // Update the selected domain with new data
      setSelectedDomain(updatedDomain);
      setIsEditMode(false);

      // Refresh the list
      await fetchDomains();
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to update domain',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditedEmail(selectedDomain?.default_from_email || '');
    setEditedName(selectedDomain?.default_from_name || '');
    setIsEditMode(false);
  };

  // Handle add/import domain
  const handleSaveDomain = async () => {
    // Validate form
    const newErrors = {
      domain: !domainForm.domain ? 'Domain is required' : '',
      organisation_id: !domainForm.organisation_id ? 'Organisation is required' : '',
      default_from_email: !domainForm.default_from_email ? 'Default sender email is required' : '',
      default_from_name: !domainForm.default_from_name ? 'Default sender name is required' : ''
    };

    setErrors(newErrors);

    // Check if we have any errors
    if (Object.values(newErrors).some(err => err)) {
      return;
    }

    try {
      setActionLoading(true);

      const requestData = {
        organisationId: domainForm.organisation_id,
        domain: domainForm.domain,
        defaultFromEmail: domainForm.default_from_email,
        defaultFromName: domainForm.default_from_name
      };

      let result: any;
      if (domainForm.dns_mode === 'manual') {
        // Manual DNS mode - register with AWS SES only
        result = await EmailDomainsService.registerDomainManual(requestData);

        // Show DNS instructions
        setInstructionsDomain(result.domain.domain);
        setDnsInstructions(result.dns_records);
        setFormattedInstructions(result.formatted_text);
        setShowInstructions(true);

        toast({
          title: 'Success',
          description: 'Domain registered for manual DNS. Send instructions to customer.',
        });
      } else if (domainForm.import_existing) {
        result = await EmailDomainsService.importDomain(requestData);
        toast({
          title: 'Success',
          description: result.instructions || 'Domain imported successfully',
        });
      } else {
        result = await EmailDomainsService.addDomain(requestData);
        toast({
          title: 'Success',
          description: result.instructions || 'Domain registered successfully',
        });
      }

      setAddDomainDialogOpen(false);
      fetchDomains(); // Refresh list
    } catch (err: any) {
      console.error('Error saving domain:', err);
      toast({
        title: 'Error',
        description: err.message || 'Failed to save domain',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Handle domain actions
  const handleVerifyNameservers = async (domain: EmailDomain) => {
    try {
      setActionLoading(true);
      const result = await EmailDomainsService.verifyNameservers(domain.id);

      toast({
        title: result.status === 'active' ? 'Verified' : 'Pending',
        description: result.message,
      });

      await fetchDomains(); // Refresh list

      // Refresh selected domain in dialog
      if (selectedDomain?.id === domain.id) {
        const updatedDomain = await EmailDomainsService.getDomain(domain.id);
        setSelectedDomain(updatedDomain);
      }
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to verify nameservers',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleSyncSESRecords = async (domain: EmailDomain) => {
    try {
      setActionLoading(true);
      const result = await EmailDomainsService.syncSESRecords(domain.id);

      toast({
        title: 'Success',
        description: `${result.records_created} DNS records synced to Cloudflare`,
      });

      await fetchDomains(); // Refresh list

      // Refresh selected domain in dialog
      if (selectedDomain?.id === domain.id) {
        const updatedDomain = await EmailDomainsService.getDomain(domain.id);
        setSelectedDomain(updatedDomain);
      }
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to sync DNS records',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCheckVerification = async (domain: EmailDomain) => {
    try {
      setActionLoading(true);
      const result = await EmailDomainsService.checkVerification(domain.id);

      toast({
        title: result.is_verified ? 'Domain Verified! ✅' : 'Verification Pending',
        description: result.is_verified
          ? 'Domain is verified and ready to send emails'
          : 'DNS records have not propagated yet. Check again in a few hours.',
      });

      await fetchDomains(); // Refresh list

      // Refresh selected domain in dialog
      if (selectedDomain?.id === domain.id) {
        const updatedDomain = await EmailDomainsService.getDomain(domain.id);
        setSelectedDomain(updatedDomain);
      }
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to check verification',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  // MAIL FROM handlers
  const handleConfigureMailFrom = async (domain: EmailDomain) => {
    try {
      setActionLoading(true);
      const result = await EmailDomainsService.configureMailFrom(domain.id, 'chef');

      toast({
        title: 'Success',
        description: result.message || 'MAIL FROM configured. Ready to sync DNS records.',
      });

      await fetchDomains(); // Refresh list

      // Refresh selected domain in dialog
      if (selectedDomain?.id === domain.id) {
        const updatedDomain = await EmailDomainsService.getDomain(domain.id);
        setSelectedDomain(updatedDomain);
      }
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to configure MAIL FROM',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleSyncMailFromDNS = async (domain: EmailDomain) => {
    try {
      setActionLoading(true);
      const result = await EmailDomainsService.syncMailFromDNS(domain.id);

      toast({
        title: 'Success',
        description: `${result.records_created} MAIL FROM DNS records synced to Cloudflare`,
      });

      await fetchDomains(); // Refresh list

      // Refresh selected domain in dialog
      if (selectedDomain?.id === domain.id) {
        const updatedDomain = await EmailDomainsService.getDomain(domain.id);
        setSelectedDomain(updatedDomain);
      }
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to sync MAIL FROM DNS',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCheckMailFromStatus = async (domain: EmailDomain) => {
    try {
      setActionLoading(true);
      const result = await EmailDomainsService.checkMailFromStatus(domain.id);

      toast({
        title: result.verified ? 'MAIL FROM Verified! ✅' : 'MAIL FROM Pending',
        description: result.message || (result.verified
          ? 'MAIL FROM is verified and ready for sending'
          : `MAIL FROM status: ${result.status}. Verification typically takes 24-48 hours.`),
      });

      await fetchDomains(); // Refresh list

      // Refresh selected domain in dialog
      if (selectedDomain?.id === domain.id) {
        const updatedDomain = await EmailDomainsService.getDomain(domain.id);
        setSelectedDomain(updatedDomain);
      }
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to check MAIL FROM status',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Manual DNS workflow handlers
  const handleConvertToManual = async (domain: EmailDomain) => {
    if (!confirm(
      `Convert ${domain.domain} to manual DNS mode?\n\n` +
      `This will switch from Cloudflare-managed DNS to manual DNS configuration. ` +
      `Cloudflare data will be preserved for historical record, but DNS records ` +
      `will need to be managed manually by the customer.`
    )) {
      return;
    }

    try {
      setActionLoading(true);

      const result = await EmailDomainsService.convertToManualDNS(domain.id);

      // Show DNS instructions
      setInstructionsDomain(result.domain.domain);
      setDnsInstructions(result.dns_records);
      setFormattedInstructions(result.formatted_text);
      setShowInstructions(true);

      toast({
        title: "Domain Converted",
        description: "Now in manual DNS mode. Send DNS instructions to customer."
      });

      await fetchDomains(); // Refresh list

      // Refresh selected domain in dialog
      if (selectedDomain?.id === domain.id) {
        const updatedDomain = await EmailDomainsService.getDomain(domain.id);
        setSelectedDomain(updatedDomain);
      }
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || 'Failed to convert domain',
        variant: "destructive"
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleShowDNSInstructions = async (domain: EmailDomain) => {
    try {
      setActionLoading(true);

      const result = await EmailDomainsService.getDNSInstructions(domain.id);

      setInstructionsDomain(result.domain);
      setDnsInstructions(result.dns_records);
      setFormattedInstructions(result.formatted_text);
      setShowInstructions(true);
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || 'Failed to get DNS instructions',
        variant: "destructive"
      });
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Mail className="h-6 w-6" />
          Email Domains
        </h2>

        <div className="flex w-full sm:w-auto gap-2 flex-wrap">
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground/70" />
            <Input
              placeholder="Search domains..."
              className="pl-8"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <Select
            value={orgFilter}
            onValueChange={(value) => setOrgFilter(value)}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Organisations" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Organisations</SelectItem>
              {organisations.map(org => (
                <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={verificationFilter}
            onValueChange={(value) => setVerificationFilter(value)}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="verified">Verified</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
            </SelectContent>
          </Select>

          <Button variant="outline" size="icon" onClick={fetchDomains}>
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Info Card */}
      <Card className="bg-muted/50">
        <CardContent className="p-4">
          <div className="flex items-start gap-2">
            <Mail className="h-5 w-5 text-primary mt-0.5" />
            <div>
              <h3 className="font-medium mb-1">Email Domain Management</h3>
              <p className="text-sm text-muted-foreground">
                Manage email sending domains for organisations. Domains must be verified with AWS SES
                and configured with Cloudflare DNS before they can be used for email campaigns.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add Domain Button */}
      <div className="flex justify-between mb-4">
        <div className="text-sm text-muted-foreground">
          {totalItems} domain{totalItems !== 1 ? 's' : ''} found
        </div>
        <Dialog open={addDomainDialogOpen} onOpenChange={setAddDomainDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <PlusCircle className="mr-2 h-4 w-4" />
              Add Domain
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]" aria-describedby="add-domain-description">
            <DialogHeader>
              <DialogTitle>Add Email Domain</DialogTitle>
              <DialogDescription id="add-domain-description">
                Register a new domain for email sending or import an existing Cloudflare zone.
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-4">
              <div>
                <Label htmlFor="domain" className="text-sm">
                  Domain*
                </Label>
                <Input
                  id="domain"
                  placeholder="example.com"
                  value={domainForm.domain}
                  onChange={(e) => setDomainForm({ ...domainForm, domain: e.target.value })}
                />
                {errors.domain && (
                  <p className="text-sm text-destructive mt-1">{errors.domain}</p>
                )}
              </div>

              <div>
                <Label htmlFor="organisation" className="text-sm">
                  Organisation*
                </Label>
                <Select
                  value={domainForm.organisation_id}
                  onValueChange={(value) => setDomainForm({ ...domainForm, organisation_id: value })}
                >
                  <SelectTrigger id="organisation">
                    <SelectValue placeholder="Select organisation" />
                  </SelectTrigger>
                  <SelectContent>
                    {organisations.map(org => (
                      <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.organisation_id && (
                  <p className="text-sm text-destructive mt-1">{errors.organisation_id}</p>
                )}
              </div>

              <div>
                <Label htmlFor="default_from_email" className="text-sm">
                  Default Sender Email*
                </Label>
                <Input
                  id="default_from_email"
                  type="email"
                  placeholder="info@example.com"
                  value={domainForm.default_from_email}
                  onChange={(e) => setDomainForm({ ...domainForm, default_from_email: e.target.value })}
                />
                {errors.default_from_email && (
                  <p className="text-sm text-destructive mt-1">{errors.default_from_email}</p>
                )}
              </div>

              <div>
                <Label htmlFor="default_from_name" className="text-sm">
                  Default Sender Name*
                </Label>
                <Input
                  id="default_from_name"
                  placeholder="Restaurant Name"
                  value={domainForm.default_from_name}
                  onChange={(e) => setDomainForm({ ...domainForm, default_from_name: e.target.value })}
                />
                {errors.default_from_name && (
                  <p className="text-sm text-destructive mt-1">{errors.default_from_name}</p>
                )}
              </div>

              <div>
                <Label htmlFor="dns_mode" className="text-sm mb-2 block">
                  DNS Management Mode*
                </Label>
                <Select
                  value={domainForm.dns_mode}
                  onValueChange={(value: 'cloudflare' | 'manual') => setDomainForm({ ...domainForm, dns_mode: value, import_existing: value === 'manual' ? false : domainForm.import_existing })}
                >
                  <SelectTrigger id="dns_mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cloudflare">
                      <div className="flex items-center gap-2">
                        <Cloud className="h-4 w-4" />
                        <span>Cloudflare (Automatic DNS)</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="manual">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        <span>Manual (Customer Manages DNS)</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {domainForm.dns_mode === 'cloudflare' && (
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="import_existing"
                    checked={domainForm.import_existing}
                    onChange={(e) => setDomainForm({ ...domainForm, import_existing: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <Label htmlFor="import_existing" className="text-sm cursor-pointer">
                    Import from existing Cloudflare zone
                  </Label>
                </div>
              )}

              <div className="p-3 bg-muted rounded-lg text-sm text-muted-foreground">
                {domainForm.dns_mode === 'manual' ? (
                  <div className="space-y-2">
                    <p className="font-medium">Manual DNS Mode:</p>
                    <p>• Domain registered with AWS SES only (no Cloudflare zone)</p>
                    <p>• Customer manages DNS records at their own provider</p>
                    <p>• DNS instructions will be provided after registration</p>
                  </div>
                ) : domainForm.import_existing ? (
                  <p>This domain already exists in Cloudflare and will be linked to the system.</p>
                ) : (
                  <p>A new Cloudflare zone will be created for this domain. You'll need to update nameservers with your domain registrar.</p>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setAddDomainDialogOpen(false)} disabled={actionLoading}>
                Cancel
              </Button>
              <Button onClick={handleSaveDomain} disabled={actionLoading}>
                {actionLoading ? 'Processing...' : (domainForm.import_existing ? 'Import Domain' : 'Register Domain')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Domains Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Domain</TableHead>
                <TableHead>Organisation</TableHead>
                <TableHead>Cloudflare Status</TableHead>
                <TableHead>SES Verified</TableHead>
                <TableHead>DNS Synced</TableHead>
                <TableHead>MAIL FROM</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array(5).fill(0).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton className="h-6 w-32" /></TableCell>
                    <TableCell><Skeleton className="h-6 w-24" /></TableCell>
                    <TableCell><Skeleton className="h-6 w-20" /></TableCell>
                    <TableCell><Skeleton className="h-6 w-16" /></TableCell>
                    <TableCell><Skeleton className="h-6 w-16" /></TableCell>
                    <TableCell><Skeleton className="h-6 w-20" /></TableCell>
                    <TableCell className="text-right"><Skeleton className="h-6 w-8 ml-auto" /></TableCell>
                  </TableRow>
                ))
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-4 text-destructive">
                    Error loading domains: {error}
                  </TableCell>
                </TableRow>
              ) : domains.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <div className="flex flex-col items-center gap-2">
                      <Mail className="h-12 w-12 text-muted-foreground/50" />
                      <p className="text-muted-foreground">No email domains found</p>
                      <p className="text-sm text-muted-foreground">Add a domain to start sending emails</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                domains.map((domain) => (
                  <TableRow key={domain.id} className="group">
                    <TableCell>
                      <button
                        onClick={() => handleViewDetails(domain)}
                        className="font-medium cursor-pointer transition-colors hover:text-primary active:text-primary"
                      >
                        {domain.domain}
                      </button>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center">
                        <span>{domain.organisation_name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={domain.cloudflare_zone_status === 'active' ? 'default' : 'secondary'}>
                        {domain.cloudflare_zone_status || 'pending'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {domain.is_verified ? (
                        <CheckCircle className="h-5 w-5 stroke-green-600 fill-none" />
                      ) : (
                        <AlertCircle className="h-5 w-5 stroke-red-600 fill-none" />
                      )}
                    </TableCell>
                    <TableCell>
                      {domain.dns_records_synced ? (
                        <CheckCircle className="h-5 w-5 stroke-green-600 fill-none" />
                      ) : (
                        <AlertCircle className="h-5 w-5 stroke-red-600 fill-none" />
                      )}
                    </TableCell>
                    <TableCell>
                      {domain.mail_from_status === 'success' ? (
                        <CheckCircle className="h-5 w-5 stroke-green-600 fill-none" />
                      ) : domain.mail_from_domain && domain.mail_from_status === 'pending' ? (
                        <AlertCircle className="h-5 w-5 stroke-yellow-600 fill-none" />
                      ) : domain.mail_from_status === 'failed' ? (
                        <AlertCircle className="h-5 w-5 stroke-red-600 fill-none" />
                      ) : (
                        <span className="text-sm text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewDetails(domain)}
                        className="opacity-0 group-hover:opacity-100"
                      >
                        <Eye className="h-4 w-4" />
                        <span className="sr-only">View Details</span>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {/* Pagination controls */}
          {!isLoading && !error && domains.length > 0 && (
            <div className="flex items-center justify-between px-4 py-4 border-t">
              <div className="text-sm text-muted-foreground">
                Showing {domains.length > 0 ? (page - 1) * pageSize + 1 : 0} - {Math.min(page * pageSize, totalItems)} of {totalItems} domains
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="text-sm">
                  Page {page} of {totalPages || 1}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Domain Details Dialog */}
      <Dialog open={viewDetailsDialogOpen} onOpenChange={setViewDetailsDialogOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto scrollbar-hide" aria-describedby="domain-details-description">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Domain Management
            </DialogTitle>
            <DialogDescription id="domain-details-description">
              View and manage domain configuration for email sending
            </DialogDescription>
          </DialogHeader>

          {selectedDomain && (
            <div className="space-y-6 py-4">
              {/* Domain Information */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium flex items-center gap-2">
                    <Globe className="h-4 w-4" />
                    Domain Information
                  </h4>
                  {!isEditMode ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsEditMode(true)}
                      className="h-8"
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                  ) : (
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleCancelEdit}
                        disabled={actionLoading}
                        className="h-8"
                      >
                        <X className="h-4 w-4 mr-1" />
                        Cancel
                      </Button>
                      <Button
                        variant="default"
                        size="sm"
                        onClick={handleSaveDomainUpdates}
                        disabled={actionLoading}
                        className="h-8"
                      >
                        <Save className="h-4 w-4 mr-1" />
                        {actionLoading ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </div>
                  )}
                </div>
                <div className="grid gap-3 text-sm bg-muted/50 p-4 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Domain:</span>
                    <span className="font-mono font-medium">{selectedDomain.domain}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Organisation:</span>
                    <span>{selectedDomain.organisation_name}</span>
                  </div>
                  <div className="flex justify-between items-start gap-4">
                    <span className="text-muted-foreground mt-2">Default Sender:</span>
                    {isEditMode ? (
                      <Input
                        type="email"
                        value={editedEmail}
                        onChange={(e) => setEditedEmail(e.target.value)}
                        className="max-w-xs"
                        placeholder="info@example.com"
                      />
                    ) : (
                      <span className="font-mono">{selectedDomain.default_from_email}</span>
                    )}
                  </div>
                  <div className="flex justify-between items-start gap-4">
                    <span className="text-muted-foreground mt-2">Default Name:</span>
                    {isEditMode ? (
                      <Input
                        value={editedName}
                        onChange={(e) => setEditedName(e.target.value)}
                        className="max-w-xs"
                        placeholder="Restaurant Name"
                      />
                    ) : (
                      <span>{selectedDomain.default_from_name}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* DNS Management Mode */}
              <div>
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Cloud className="h-4 w-4" />
                  DNS Management
                </h4>
                <div className="space-y-3">
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <div className="font-medium mb-2">DNS Management Mode</div>
                    {selectedDomain.cloudflare_zone_id ? (
                      <div className="space-y-2">
                        <Badge variant="default">Cloudflare Managed</Badge>
                        <p className="text-sm text-muted-foreground">
                          DNS records are synced automatically via Cloudflare API
                        </p>

                        {/* Zone Status */}
                        <div className="flex items-center justify-between pt-2">
                          <span className="text-sm text-muted-foreground">Zone Status:</span>
                          <Badge variant={selectedDomain.cloudflare_zone_status === 'active' ? 'default' : 'secondary'}>
                            {selectedDomain.cloudflare_zone_status || 'pending'}
                          </Badge>
                        </div>

                        {/* Option to convert to manual */}
                        <Button
                          onClick={() => handleConvertToManual(selectedDomain)}
                          variant="outline"
                          size="sm"
                          disabled={actionLoading}
                          className="w-full mt-2"
                        >
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Convert to Manual DNS
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Badge variant="secondary">Manual DNS</Badge>
                        <p className="text-sm text-muted-foreground">
                          Customer manages DNS records independently
                        </p>

                        {/* Show DNS instructions button */}
                        <Button
                          onClick={() => handleShowDNSInstructions(selectedDomain)}
                          variant="outline"
                          size="sm"
                          disabled={actionLoading}
                          className="w-full mt-2"
                        >
                          <FileText className="h-4 w-4 mr-2" />
                          View DNS Instructions
                        </Button>
                      </div>
                    )}
                  </div>

                  {selectedDomain.cloudflare_nameservers && (
                    <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                      <div className="font-medium mb-2 text-sm">Nameservers:</div>
                      <div className="font-mono text-xs space-y-1">
                        {selectedDomain.cloudflare_nameservers.map((ns: string, i: number) => (
                          <div key={i}>{ns}</div>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedDomain.cloudflare_zone_id && selectedDomain.cloudflare_zone_status !== 'active' && (
                    <Button
                      onClick={() => handleVerifyNameservers(selectedDomain)}
                      disabled={actionLoading}
                      variant="outline"
                      className="w-full"
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Verify Nameservers
                    </Button>
                  )}
                </div>
              </div>

              {/* AWS SES Status */}
              <div>
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  AWS SES Status
                </h4>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-4 bg-muted/50 rounded-lg">
                      <div className="font-medium mb-1">DNS Synced</div>
                      <div className="flex items-center gap-2">
                        {selectedDomain.dns_records_synced ? (
                          <CheckCircle className="h-5 w-5 stroke-green-600 fill-none" />
                        ) : (
                          <AlertCircle className="h-5 w-5 stroke-red-600 fill-none" />
                        )}
                        <span className="text-sm">
                          {selectedDomain.dns_records_synced ? 'Yes' : 'No'}
                        </span>
                      </div>
                    </div>

                    <div className="p-4 bg-muted/50 rounded-lg">
                      <div className="font-medium mb-1">Verified</div>
                      <div className="flex items-center gap-2">
                        {selectedDomain.is_verified ? (
                          <CheckCircle className="h-5 w-5 stroke-green-600 fill-none" />
                        ) : (
                          <AlertCircle className="h-5 w-5 stroke-red-600 fill-none" />
                        )}
                        <span className="text-sm">
                          {selectedDomain.is_verified ? 'Yes' : 'No'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* MAIL FROM Status */}
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <div className="font-medium mb-2">MAIL FROM Configuration</div>
                    {selectedDomain.mail_from_domain ? (
                      <div className="space-y-2">
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-muted-foreground">Domain:</span>
                          <span className="font-mono">{selectedDomain.mail_from_domain}</span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-muted-foreground">Status:</span>
                          <Badge variant={selectedDomain.mail_from_status === 'success' ? 'default' : 'secondary'}>
                            {selectedDomain.mail_from_status}
                          </Badge>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">Not configured</p>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-2">
                    {/* Cloudflare Mode - Show Sync Button */}
                    {selectedDomain.cloudflare_zone_id && !selectedDomain.dns_records_synced && selectedDomain.cloudflare_zone_status === 'active' && (
                      <Button
                        onClick={() => handleSyncSESRecords(selectedDomain)}
                        disabled={actionLoading}
                        className="w-full"
                      >
                        <Server className="h-4 w-4 mr-2" />
                        Sync SES DNS Records
                      </Button>
                    )}

                    {/* Manual DNS Mode - Show alert and check button immediately */}
                    {!selectedDomain.cloudflare_zone_id && !selectedDomain.is_verified && (
                      <>
                        <Alert>
                          <Info className="h-4 w-4" />
                          <AlertDescription>
                            Customer must add DNS records manually.
                            Use "View DNS Instructions" to get the records.
                          </AlertDescription>
                        </Alert>

                        <Button
                          onClick={() => handleCheckVerification(selectedDomain)}
                          disabled={actionLoading}
                          variant="outline"
                          className="w-full"
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Check AWS SES Verification
                        </Button>
                      </>
                    )}

                    {/* Cloudflare Mode - Show check button after DNS synced */}
                    {selectedDomain.cloudflare_zone_id && !selectedDomain.is_verified && selectedDomain.dns_records_synced && (
                      <Button
                        onClick={() => handleCheckVerification(selectedDomain)}
                        disabled={actionLoading}
                        variant="outline"
                        className="w-full"
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Check Verification Status
                      </Button>
                    )}

                    {selectedDomain.is_verified && (
                      <div className="p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
                        <div className="flex items-center gap-2 text-green-800 dark:text-green-200">
                          <CheckCircle className="h-5 w-5" />
                          <span className="font-medium">Domain is verified and ready to send emails!</span>
                        </div>
                      </div>
                    )}

                    {/* MAIL FROM Action Buttons */}
                    {selectedDomain.is_verified && !selectedDomain.mail_from_domain && (
                      <Button
                        onClick={() => handleConfigureMailFrom(selectedDomain)}
                        disabled={actionLoading}
                        variant="outline"
                        className="w-full"
                      >
                        <Mail className="h-4 w-4 mr-2" />
                        Configure MAIL FROM
                      </Button>
                    )}

                    {selectedDomain.mail_from_domain && selectedDomain.mail_from_status === 'pending' && selectedDomain.cloudflare_zone_id && (
                      <Button
                        onClick={() => handleSyncMailFromDNS(selectedDomain)}
                        disabled={actionLoading}
                        variant="outline"
                        className="w-full"
                      >
                        <Server className="h-4 w-4 mr-2" />
                        Sync MAIL FROM DNS
                      </Button>
                    )}

                    {selectedDomain.mail_from_domain && selectedDomain.mail_from_status === 'pending' && (
                      <Button
                        onClick={() => handleCheckMailFromStatus(selectedDomain)}
                        disabled={actionLoading}
                        variant="outline"
                        className="w-full"
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Check MAIL FROM Status
                      </Button>
                    )}
                  </div>

                  {selectedDomain.dns_records_synced && !selectedDomain.is_verified && (
                    <div className="p-4 bg-yellow-50 dark:bg-yellow-950/20 rounded-lg border border-yellow-200 dark:border-yellow-800 text-sm">
                      <p className="text-yellow-800 dark:text-yellow-200">
                        DNS records have been synced. Verification typically takes 24-48 hours for DNS propagation.
                        Check back later or click "Check Verification Status" to poll AWS.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDetailsDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DNS Instructions Dialog */}
      <Dialog open={showInstructions} onOpenChange={setShowInstructions}>
        <DialogContent className="max-w-[min(90vw,1400px)] max-h-[90vh] overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              DNS Records for {instructionsDomain}
            </DialogTitle>
            <DialogDescription>
              Copy these records and send to the customer to add to their DNS provider
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Action Buttons */}
            <div className="flex gap-2">
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(formattedInstructions);
                  toast({
                    title: "Copied!",
                    description: "DNS instructions copied to clipboard"
                  });
                }}
              >
                <Copy className="h-4 w-4 mr-2" />
                Copy All Records
              </Button>
            </div>

            {/* Formatted Text View */}
            <div>
              <Label>Copy-Paste Format (for email/chat)</Label>
              <pre className="p-4 bg-muted rounded-lg overflow-auto text-xs font-mono whitespace-pre-wrap mt-2 max-h-96 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                {formattedInstructions}
              </pre>
            </div>

            {/* Table View */}
            <div>
              <Label>DNS Records Summary</Label>
              <div className="border rounded-lg mt-2 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Value</TableHead>
                      <TableHead>Priority</TableHead>
                      <TableHead>Proxy</TableHead>
                      <TableHead>Required</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dnsInstructions.map((record: any, i: number) => (
                      <TableRow key={i} className={!record.required ? 'bg-muted/30' : ''}>
                        <TableCell>
                          <Badge variant={
                            record.type === 'MX' ? 'default' :
                            record.type === 'A' ? 'secondary' :
                            'outline'
                          }>
                            {record.type}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-xs max-w-xs truncate" title={record.name}>
                          {record.name}
                        </TableCell>
                        <TableCell className="font-mono text-xs max-w-md truncate" title={record.value}>
                          {record.value}
                        </TableCell>
                        <TableCell>{record.priority || '-'}</TableCell>
                        <TableCell>
                          {record.proxy === 'DNS only' ? (
                            <Badge variant="destructive" className="text-xs">
                              DNS only ⚠️
                            </Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">{record.proxy}</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {record.required ? (
                            <CheckCircle className="h-4 w-4 stroke-green-600" />
                          ) : (
                            <span className="text-xs text-muted-foreground">Optional</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Warnings */}
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4 stroke-white"/>
              <AlertTitle className="text-white">Critical Requirements</AlertTitle>
              <AlertDescription className="space-y-1 [&>p]:text-white">
                <p>• All CNAME records (DKIM) must be set to "DNS only" (NOT proxied)</p>
                <p>• All A records (order/review) must be set to "DNS only" (NOT proxied)</p>
                <p>• Check for existing DMARC record before adding</p>
                <p>• Do NOT add MX records to root domain if customer has existing email</p>
              </AlertDescription>
            </Alert>

            {/* Category Breakdown */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
                <div className="font-medium mb-2 text-blue-900 dark:text-blue-100">
                  Required Records
                </div>
                <div className="text-sm text-blue-800 dark:text-blue-200">
                  {dnsInstructions.filter((r: any) => r.required).length} records must be added
                </div>
              </div>
              <div className="p-4 bg-amber-50 dark:bg-amber-950/20 rounded-lg">
                <div className="font-medium mb-2 text-amber-900 dark:text-amber-100">
                  Optional Records
                </div>
                <div className="text-sm text-amber-800 dark:text-amber-200">
                  {dnsInstructions.filter((r: any) => !r.required).length} recommended but not required
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInstructions(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminEmailDomainsTab;
